export const STANDARD_AI_RUN_CREDITS = 10;

export type PlanKey = "free" | "solo" | "startup" | "business";
export type PublicPlanKey = PlanKey | "enterprise";
export type BillingPeriod = "monthly" | "annual";

export interface PlanEntitlements {
  editors: number | "custom";
  viewers: number | "unlimited" | "custom";
  aiRuns: number | "custom";
  quickClarity: "limited" | "included" | "custom";
  products: number | "unlimited" | "custom";
  projects: number | "unlimited" | "custom";
  retention: string;
  exports: string[];
  support: string;
}

export interface SubscriptionTierConfig {
  name: string;
  publicName: string;
  slug: string;
  product_ids: string[];
  price_id_monthly: string | null;
  price_id_annual: string | null;
  monthlyPrice: number;
  annualPrice: number;
  monthlyCredits: number;
  monthlyAiRuns: number;
  description: string;
  bestFor: string;
  features: string[];
  entitlements: PlanEntitlements;
}

export const SUBSCRIPTION_TIERS = {
  free: {
    name: "Free",
    publicName: "Free",
    slug: "free",
    product_ids: [] as string[],
    price_id_monthly: null,
    price_id_annual: null,
    monthlyPrice: 0,
    annualPrice: 0,
    monthlyAiRuns: 10,
    monthlyCredits: 10 * STANDARD_AI_RUN_CREDITS,
    description: "For trying the requirements workflow.",
    bestFor: "Trying Reqqy",
    features: [
      "10 AI runs/month",
      "1 editor and 3 viewers",
      "Limited quick clarity checks",
      "Basic product/project context",
      "Markdown export",
    ],
    entitlements: {
      editors: 1,
      viewers: 3,
      aiRuns: 10,
      quickClarity: "limited",
      products: 1,
      projects: 2,
      retention: "Last 10 runs",
      exports: ["Markdown"],
      support: "Self-serve",
    },
  },
  solo: {
    name: "Solo",
    publicName: "Solo",
    slug: "solo",
    product_ids: ["prod_U3DtUXGvHQIHWY", "prod_U3DutOWpAW6Pmv"],
    price_id_monthly: "price_1T57RvPJ0ZCzH9fvuiLWB57V",
    price_id_annual: "price_1T57SgPJ0ZCzH9fvmJnjoxgx",
    monthlyPrice: 29,
    annualPrice: 290,
    monthlyAiRuns: 50,
    monthlyCredits: 50 * STANDARD_AI_RUN_CREDITS,
    description: "For individual PMs, founders, and consultants.",
    bestFor: "Individual builders",
    features: [
      "50 AI runs/month",
      "1 editor and unlimited viewers",
      "Quick clarity included",
      "Generation, deep evaluation, and refinement",
      "Markdown, CSV, and Notion-ready exports",
      "Credit top-ups available",
    ],
    entitlements: {
      editors: 1,
      viewers: "unlimited",
      aiRuns: 50,
      quickClarity: "included",
      products: 3,
      projects: 10,
      retention: "90 days",
      exports: ["Markdown", "CSV", "Notion"],
      support: "Email",
    },
  },
  startup: {
    name: "Team",
    publicName: "Team",
    slug: "team",
    product_ids: ["prod_U3Du4m8vxprRLf", "prod_U3DvIUKnMRfEBF"],
    price_id_monthly: "price_1T57T4PJ0ZCzH9fvy6Tr7zLX",
    price_id_annual: "price_1T57TPPJ0ZCzH9fvQ9d6UsLV",
    monthlyPrice: 99,
    annualPrice: 990,
    monthlyAiRuns: 200,
    monthlyCredits: 200 * STANDARD_AI_RUN_CREDITS,
    description: "For small software teams and agencies.",
    bestFor: "Small teams",
    features: [
      "200 AI runs/month",
      "3 editors and unlimited viewers",
      "Shared product and project workspaces",
      "Version history and changelogs",
      "Viewer comments and feedback",
      "Jira, Linear, and Notion-ready exports",
    ],
    entitlements: {
      editors: 3,
      viewers: "unlimited",
      aiRuns: 200,
      quickClarity: "included",
      products: 10,
      projects: 50,
      retention: "12 months",
      exports: ["Markdown", "CSV", "Jira CSV", "Linear CSV", "Notion", "JSON"],
      support: "Priority email",
    },
  },
  business: {
    name: "Business",
    publicName: "Business",
    slug: "business",
    product_ids: ["prod_U3Dvv1KRQ3cfsV", "prod_U3DvK03M4Rh3CS"],
    price_id_monthly: "price_1T57TgPJ0ZCzH9fvOtzx8qV3",
    price_id_annual: "price_1T57U7PJ0ZCzH9fvmpmS5BNK",
    monthlyPrice: 299,
    annualPrice: 2990,
    monthlyAiRuns: 750,
    monthlyCredits: 750 * STANDARD_AI_RUN_CREDITS,
    description: "For growing product organizations.",
    bestFor: "Growing product orgs",
    features: [
      "750 AI runs/month",
      "10 editors and unlimited viewers",
      "Advanced context controls",
      "Team libraries and templates",
      "Review workflows",
      "Advanced exports and usage dashboard",
    ],
    entitlements: {
      editors: 10,
      viewers: "unlimited",
      aiRuns: 750,
      quickClarity: "included",
      products: "unlimited",
      projects: "unlimited",
      retention: "24 months",
      exports: ["Markdown", "CSV", "Jira CSV", "Linear CSV", "Notion", "JSON"],
      support: "Priority support",
    },
  },
} as const satisfies Record<PlanKey, SubscriptionTierConfig>;

export type SubscriptionTier = keyof typeof SUBSCRIPTION_TIERS;

export const PLAN_ORDER: SubscriptionTier[] = ["free", "solo", "startup", "business"];

export const ENTERPRISE_PLAN = {
  id: "enterprise" as const,
  name: "Enterprise",
  monthlyPrice: null,
  annualPrice: null,
  description: "For security, procurement, and custom workflows.",
  bestFor: "Security-heavy organizations",
  features: [
    "Custom editor/viewer setup",
    "Custom AI run allocation",
    "SSO/SAML and custom retention",
    "Private deployment options",
    "SLA and dedicated onboarding",
    "Custom export/integration roadmap",
  ],
};

export const TOPUP_CREDIT_PACKS = [
  // These keep the currently configured Stripe top-up prices intact.
  // Create new Stripe prices before changing these to the desired 100/250/600/1500 packs.
  { name: "Small", legacyName: "Small", credits: 50, priceCents: 1500, extraAiRuns: 5, tagline: "5 extra AI runs" },
  { name: "Medium", legacyName: "Medium", credits: 150, priceCents: 3900, extraAiRuns: 15, tagline: "15 extra AI runs" },
  { name: "Large", legacyName: "Large", credits: 500, priceCents: 9900, extraAiRuns: 50, tagline: "50 extra AI runs" },
  { name: "Bulk", legacyName: "Bulk", credits: 1500, priceCents: 24900, extraAiRuns: 150, tagline: "150 extra AI runs" },
] as const;

export function creditsToAiRuns(credits: number): number {
  if (!Number.isFinite(credits)) return 0;
  return credits / STANDARD_AI_RUN_CREDITS;
}

export function aiRunsToCredits(aiRuns: number): number {
  if (!Number.isFinite(aiRuns)) return 0;
  return aiRuns * STANDARD_AI_RUN_CREDITS;
}

export function formatAiRunsFromCredits(credits: number, options: { compact?: boolean } = {}): string {
  const runs = creditsToAiRuns(credits);
  const rounded = Math.abs(runs) >= 10 ? Math.floor(runs) : Number(runs.toFixed(1));
  const label = options.compact ? "runs" : rounded === 1 ? "AI run" : "AI runs";
  return `${rounded.toLocaleString()} ${label}`;
}

export function formatPlanLimit(value: number | "unlimited" | "custom"): string {
  if (value === "unlimited") return "Unlimited";
  if (value === "custom") return "Custom";
  return value.toLocaleString();
}

export function planPrice(plan: SubscriptionTier, billingPeriod: BillingPeriod): number {
  const tier = SUBSCRIPTION_TIERS[plan];
  return billingPeriod === "annual" ? tier.annualPrice : tier.monthlyPrice;
}

export type PlanComparisonValue =
  | boolean
  | string
  | { label: string; included?: boolean; note?: string; highlight?: boolean };

export interface PlanComparisonRow {
  label: string;
  description?: string;
  values: Record<PublicPlanKey, PlanComparisonValue>;
}

export interface PlanComparisonSection {
  title: string;
  rows: PlanComparisonRow[];
}

export const PLAN_COMPARISON_SECTIONS: PlanComparisonSection[] = [
  {
    title: "Usage & access",
    rows: [
      { label: "Price", values: { free: "€0", solo: "€29/mo", startup: "€99/mo", business: "€299/mo", enterprise: "Custom" } },
      { label: "Editors", description: "Users who can create, evaluate, refine, and export work.", values: { free: "1", solo: "1", startup: "3 included", business: "10 included", enterprise: "Custom" } },
      { label: "Viewers", description: "People who can read, review, and comment without consuming AI runs.", values: { free: "3", solo: "Unlimited", startup: "Unlimited", business: "Unlimited", enterprise: "Custom" } },
      { label: "AI runs / month", description: "Generation, deep evaluation, and refinement actions.", values: { free: "10", solo: "50", startup: "200", business: "750", enterprise: "Custom" } },
      { label: "Quick clarity", values: { free: "Limited", solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Extra usage", values: { free: { label: "Upgrade", included: false }, solo: "Top-ups", startup: "Top-ups", business: "Top-ups / invoiced", enterprise: "Custom" } },
    ],
  },
  {
    title: "Requirements workflows",
    rows: [
      { label: "Generate from idea", values: { free: "Basic", solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Review existing PRD", values: { free: false, solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Deep readiness evaluation", values: { free: false, solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Refinement runs", values: { free: false, solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Selected-story refinement", values: { free: false, solo: true, startup: true, business: true, enterprise: true } },
      { label: "Project-wide refinement", values: { free: false, solo: "Limited", startup: true, business: true, enterprise: "Custom" } },
    ],
  },
  {
    title: "Context layer",
    rows: [
      { label: "Product knowledge atoms", values: { free: "Basic", solo: true, startup: "Shared", business: "Advanced", enterprise: "Custom" } },
      { label: "Project context atoms", values: { free: "Basic", solo: true, startup: "Shared", business: "Advanced", enterprise: "Custom" } },
      { label: "Context inbox", description: "Suggestions from reviews/refinements that can become reusable context.", values: { free: "Limited", solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Context extraction from docs", values: { free: false, solo: "Counts as run", startup: "Counts as run", business: "Advanced", enterprise: "Custom" } },
      { label: "Run context trace", values: { free: "Basic", solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Context governance", values: { free: false, solo: "Basic", startup: "Team", business: "Advanced", enterprise: "Custom" } },
    ],
  },
  {
    title: "Collaboration & history",
    rows: [
      { label: "Version history", values: { free: "Limited", solo: true, startup: true, business: "Advanced", enterprise: "Custom" } },
      { label: "Diff & changelog", values: { free: false, solo: true, startup: true, business: true, enterprise: true } },
      { label: "Restore previous version", values: { free: false, solo: true, startup: true, business: true, enterprise: true } },
      { label: "Comments / viewer feedback", values: { free: false, solo: true, startup: true, business: true, enterprise: true } },
      { label: "Approval workflows", values: { free: false, solo: false, startup: "Basic", business: "Advanced", enterprise: "Custom" } },
      { label: "Retention", values: { free: "Last 10 runs", solo: "90 days", startup: "12 months", business: "24 months", enterprise: "Custom" } },
    ],
  },
  {
    title: "Exports",
    rows: [
      { label: "Markdown", values: { free: true, solo: true, startup: true, business: true, enterprise: true } },
      { label: "CSV", values: { free: false, solo: true, startup: true, business: true, enterprise: true } },
      { label: "JSON", values: { free: false, solo: false, startup: true, business: true, enterprise: true } },
      { label: "Notion-ready formats", values: { free: false, solo: true, startup: true, business: true, enterprise: "Custom" } },
      { label: "Jira-ready CSV", values: { free: false, solo: false, startup: true, business: true, enterprise: "Custom" } },
      { label: "Linear-ready CSV", values: { free: false, solo: false, startup: true, business: true, enterprise: "Custom" } },
    ],
  },
  {
    title: "Workspace controls",
    rows: [
      { label: "Usage dashboard", values: { free: "Basic", solo: true, startup: true, business: "Advanced", enterprise: "Custom" } },
      { label: "Workspace roles", values: { free: "Owner only", solo: "Owner only", startup: "Owner/Admin/Editor/Viewer", business: "Owner/Admin/Editor/Viewer", enterprise: "Custom roles" } },
      { label: "Product/project permissions", values: { free: false, solo: false, startup: "Basic", business: "Advanced", enterprise: "Custom" } },
      { label: "SSO/SAML", values: { free: false, solo: false, startup: false, business: "Roadmap", enterprise: true } },
      { label: "Support", values: { free: "Self-serve", solo: "Email", startup: "Priority email", business: "Priority support", enterprise: "Dedicated" } },
    ],
  },
];
